import { Transition } from '@headlessui/react';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { getCalibrationLevel } from 'libs/end-to-end/src/calibration-level';
import { getCalibrationScore } from 'libs/end-to-end/src/tests-utils';
import { createTreatmentKey } from 'libs/end-to-end/src/treatment';
import { getTestCounts } from 'libs/end-to-end/src/tests-utils';
import { impactFactorAsNumber } from 'libs/end-to-end/src/impact-factor';
import { predictCalibrationScore } from 'libs/end-to-end/src/calibration-level';
import React, { useContext, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Masonry from 'react-masonry-css';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { PolarChart } from 'apps/sigma2-ui/src/components/PolarChart';
import { PredictionsPolarChartContext } from 'apps/sigma2-ui/src/components/PolarChart';
import { PredictionsPolarDataPoint } from 'apps/sigma2-ui/src/components/PolarChart';
import { SelectedTreatments } from 'apps/sigma2-ui/src/components/SelectedTreatments';
import { SelectedTreatmentsVariant } from 'apps/sigma2-ui/src/components/SelectedTreatments';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { BIAS_TYPE_MESSAGE_IDS } from 'apps/sigma2-ui/src/constants/data';
import { TREATMENT_MESSAGE_IDS } from 'apps/sigma2-ui/src/constants/data';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { IconArrowLeft24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconArrowLeft24px';
import { RadarSliceTooltipProps } from '@nivo/radar';
import { CalibrationLevelPill } from 'apps/sigma2-ui/src/components/utils/CalibrationLevelPill';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';

const PredictionsFocusAreaTooltip = ({ index }: RadarSliceTooltipProps) => {
  const focusArea = FocusArea.parse(index);

  // Pulls in PolarDataPoint[] from context
  const context = useContext(PredictionsPolarChartContext);
  const language = useLanguage();
  const { myObservation } = useObservation();
  const activeTreatmentsCount =
    myObservation?.treatments?.filter(
      (treatment) =>
        treatment.status === 'Active' && treatment.focusArea === focusArea,
    ).length ?? 0;

  const focusAreaData = context.dataPoints.find((x) => x.label === focusArea);

  if (!focusAreaData) {
    return null;
  }

  const calibrationLevel = getCalibrationLevel(
    focusAreaData.calibrationScore ?? 0,
  );

  const passedTestCount = focusAreaData.passingTests;
  const failedTestCount = focusAreaData.failingTests;

  const passedTestsStatement =
    passedTestCount === 1
      ? language.predictionsChartTooltipPassedSingular({
          count: passedTestCount ?? 0,
        })
      : language.predictionsChartTooltipPassedPlural({
          count: passedTestCount ?? 0,
        });

  const failedTestsStatement =
    failedTestCount === 1
      ? language.predictionsChartTooltipFailedSingular({
          count: failedTestCount ?? 0,
        })
      : language.predictionsChartTooltipFailedPlural({
          count: failedTestCount ?? 0,
        });

  const activeTreatmentsStatement =
    activeTreatmentsCount === 1
      ? language.predictionsChartTooltipActiveSingular({
          count: activeTreatmentsCount,
        })
      : language.predictionsChartTooltipActivePlural({
          count: activeTreatmentsCount,
        });

  return (
    <div
      className="filter shadow-8px bg-primary text-left p-4 font-sans text-white bg-opacity-50 backdrop-blur-sm rounded-md"
      data-qa={`predictionsChartTooltip${focusArea}`}
    >
      <p
        className="text-xl font-bold font-sans"
        data-qa={`predictionsChartTooltip${focusArea}Title`}
      >
        {focusArea}
      </p>
      <CalibrationLevelPill
        calibrationLevel={calibrationLevel}
        isPrediction
        data-qa={`predictionsChartTooltip${focusArea}Pill`}
        calibrationFraming={context.calibrationFraming}
      />

      <ul className="list-item list-disc mx-4">
        <li data-qa={`predictionsChartTooltip${focusArea}PassedTestsStatement`}>
          {passedTestsStatement}
        </li>
        <li data-qa={`predictionsChartTooltip${focusArea}FailedTestsStatement`}>
          {failedTestsStatement}
        </li>
        <li
          data-qa={`predictionsChartTooltip${focusArea}ActiveTreatmentsStatement`}
        >
          {activeTreatmentsStatement}
        </li>
      </ul>
    </div>
  );
};

export const TreatmentCenterPage = () => {
  useAuthenticatedPageRedirect();

  const language = useLanguage();
  const navigate = useNavigateSafe();

  const { myUser } = useUser();

  const { myObservation } = useObservation();

  const { getDimensionByColumnHeaderValue } = useDimensionConfig();

  const activeTreatmentBricks = useMemo(() => {
    if (
      !myUser ||
      !myUser.organization ||
      !myObservation ||
      !myObservation.treatments
    ) {
      return null;
    }

    const bricks: React.ReactNode[] = [];

    for (const treatment of myObservation.treatments) {
      const treatmentKey = createTreatmentKey(treatment);

      if (treatment.status !== 'Active') {
        continue;
      }

      const dimensionDefinition = getDimensionByColumnHeaderValue(
        treatment.dimension,
      );

      const levelDefinition = dimensionDefinition?.levels.find(
        (level) => level.columnValue === treatment.level,
      );

      const focusAreaDefinition = myObservation.focusAreas.find(
        (f) => f.focusArea === treatment.focusArea,
      );

      if (
        levelDefinition === undefined ||
        dimensionDefinition === undefined ||
        focusAreaDefinition === undefined
      ) {
        continue;
      }

      bricks.push(
        <Transition
          show={true}
          enter="transition-opacity duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            data-qa="solutionCard"
            key={treatmentKey}
            className="rounded-lg p-6 font-sans"
            style={{
              backgroundImage:
                'linear-gradient(180deg, rgba(50,8,92,1) 0%, rgba(50,8,92,0) 100%)',
            }}
          >
            <h3 className="text-lg" data-qa="solutionCardTitle">
              {language[TREATMENT_MESSAGE_IDS[treatment.type]]()}
            </h3>
            <p
              className="text-xs mt-2 text-gray-100"
              data-qa="solutionCardBlurb"
            >
              {focusAreaDefinition.displayName}
              {' • '}
              <span className="whitespace-nowrap">
                {dimensionDefinition.displayName}
                {': '}
                {levelDefinition.displayName}
              </span>{' '}
              {language[BIAS_TYPE_MESSAGE_IDS[treatment.biasType]]()}{' '}
              {language
                .bias({ calibrationFraming: myObservation.calibrationFraming })
                .toLowerCase()}
            </p>
            <div
              className="treatment-abstract text-gray-200 text-sm pt-6"
              dangerouslySetInnerHTML={{
                __html: String(treatment.description),
              }}
            />
          </div>
        </Transition>,
      );
    }

    return bricks;
  }, [getDimensionByColumnHeaderValue, language, myObservation, myUser]);

  const chart = useMemo(() => {
    if (
      !myUser ||
      !myUser.organization ||
      !myObservation ||
      !myObservation.treatments ||
      !myObservation.testSummary
    ) {
      return <></>;
    }

    const dataPoints: PredictionsPolarDataPoint[] = [];

    for (const focusArea of myObservation.focusAreas) {
      const focusAreaName = focusArea.focusArea;

      const focusAreaTreatments = myObservation.treatments.filter(
        (treatment) => treatment.focusArea === focusAreaName,
      );

      const focusAreaTests = myObservation.testSummary.tests.filter(
        (test) => test.focusArea === focusAreaName,
      );

      const focusAreaTestCounts = getTestCounts(focusAreaTests);

      const impactFactorSum = focusAreaTreatments
        .filter((t) => t.status === 'Active')
        .map((t) => impactFactorAsNumber(t.impactFactor))
        .reduce((partialSum, a) => partialSum + a, 0);

      const calibrationScore = getCalibrationScore({
        testCount: focusAreaTestCounts,
      });
      const predictedCalibrationScore = predictCalibrationScore({
        calibrationScore,
        impactFactor: impactFactorSum,
      });

      dataPoints.push({
        label: focusAreaName,
        calibrationScore: calibrationScore,
        predictedCalibrationScore: predictedCalibrationScore,
        passingTests: focusAreaTestCounts.passing,
        incompleteTests: focusAreaTestCounts.incomplete,
        failingTests: focusAreaTestCounts.failing,
      });
    }

    return (
      <PolarChart
        dataPoints={dataPoints}
        variant={'predictions'}
        tooltip={PredictionsFocusAreaTooltip}
        calibrationFraming={myObservation.calibrationFraming}
        dataTestId={'treatmentCenterPolarChart'}
      />
    );
  }, [myObservation, myUser]);

  const analytics = useAnalytics();

  useEffect(() => {
    analytics.trackEvent({
      eventName: 'Treatment Center Page Viewed',
      eventProperties: {},
    });
  }, [analytics]);

  return (
    <div
      className="w-full min-h-screen bg-blue-1000 text-white"
      style={{
        minWidth: '64rem',
      }}
    >
      <UserAnalysesOverlay onUserAnalysisCancel={() => {}} />

      <Helmet>
        <title>{documentTitle(language.treatmentCenter())}</title>
      </Helmet>
      <div
        className="w-full p-6 mx-auto flex relative"
        style={{ maxWidth: '87.75rem', minWidth: '64rem' }}
      >
        <LegacyButton
          onClick={(e) => {
            e.preventDefault();
            navigate(-1);
          }}
          className="fixed top-10 text-sm z-standout"
          variant={LegacyButtonVariant.PRIMARY_DARK}
        >
          <IconArrowLeft24pxSvg svg={{ className: 'w-4 inline-block mr-2' }} />
          {language.backToDashboardLink()}
        </LegacyButton>
        <div className="flex-grow">
          <div className="flex justify-center" style={{ minHeight: '45rem' }}>
            <div className="w-full max-w-[690px]">{chart}</div>
          </div>
          <Masonry
            data-qa="solutionMasonryList"
            breakpointCols={3}
            className="masonry-grid pr-8"
            columnClassName="masonry-grid__column"
          >
            {activeTreatmentBricks}
          </Masonry>
        </div>
        <div
          className="w-96 flex-shrink-0 rounded-lg flex flex-col font-sans text-white bg-blue-900 bg-opacity-50 overflow-y-auto sticky top-6 p-px border-b-1 border-secondary border-opacity-60"
          style={{ height: 'calc(100vh - 3rem)' }}
        >
          <h3 className="relative font-sans leading-6 font-normal text-lg p-6 flex justify-between items-center rounded-t-lg border-b-1 border-secondary border-opacity-50">
            {language.treatmentCenter()}
          </h3>
          <div className="pl-6 pr-6 pb-6 text-base relative overflow-y-auto flex-grow rounded-b-lg">
            <p className="text-sm text-gray-100 mt-4">
              {language.treatmentCenterIntroBlurb()}
            </p>
            <SelectedTreatments variant={SelectedTreatmentsVariant.SIDEBAR} />
          </div>
        </div>
      </div>
    </div>
  );
};
